import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { SideNav } from "@/components/SideNav";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, RefreshCw, Package } from "lucide-react";

const bandColor = (b) => ({
  critical: "bg-red-100 text-red-800 border-red-300",
  moderate: "bg-amber-100 text-amber-800 border-amber-300",
  good:     "bg-emerald-100 text-emerald-800 border-emerald-300",
  no_data:  "bg-gray-100 text-gray-700 border-gray-300",
})[b] || "bg-gray-100";

export default function AdminRestock() {
  const [rows, setRows] = useState([]);
  const [threshold, setThreshold] = useState(3);
  const [loading, setLoading] = useState(false);
  const [zone, setZone] = useState("__all__");
  const [city, setCity] = useState("__all__");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (zone !== "__all__") qs.set("zone", zone);
      if (city !== "__all__") qs.set("city", city);
      const { data } = await api.get(`/inventory/restock-needed?${qs.toString()}`);
      setRows(data.rows || []);
      setThreshold(data.threshold_days || 3);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  }, [zone, city]);

  useEffect(() => { load(); }, [load]);

  const zones = Array.from(new Set(rows.map(r => r.zone).filter(Boolean)));
  // Task 62 — case + whitespace insensitive de-dup; display in UPPERCASE. Keeps a single
  // "LUCKNOW" option even while history still carries mixed-case values.
  const cities = Array.from(new Set(
    rows.map(r => (r.city || "").trim().toUpperCase()).filter(Boolean)
  ));
  const filtered = rows.filter(r => !q ||
    (r.store_name || "").toLowerCase().includes(q.toLowerCase()) ||
    (r.store_code || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <SideNav />
      <main className="pt-16 pl-0 lg:pl-64">
        <div className="p-4 lg:p-8 max-w-7xl mx-auto space-y-6" data-testid="admin-restock-page">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-2xl lg:text-3xl font-display font-bold text-gray-900">Restock Needed</h1>
              <p className="text-sm text-gray-600 mt-1">
                Stores with &lt; {threshold} days of BIB/CO2 cover — worst first. Auto-derived from dispatch history + cups sold + physical swaps. No promoter counts are read.
              </p>
            </div>
            <button data-testid="restock-refresh-btn" onClick={load}
              className="rounded-lg bg-brand-red text-white px-4 h-10 text-sm inline-flex items-center gap-2 hover:bg-[#B31721]">
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
          </div>

          <Card className="p-4 rounded-2xl">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-gray-500">Zone</label>
                <Select value={zone} onValueChange={setZone}>
                  <SelectTrigger data-testid="restock-zone-filter" className="h-11 mt-1"><SelectValue placeholder="All zones" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All zones</SelectItem>
                    {zones.map(z => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-gray-500">City</label>
                <Select value={city} onValueChange={setCity}>
                  <SelectTrigger data-testid="restock-city-filter" className="h-11 mt-1"><SelectValue placeholder="All cities" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All cities</SelectItem>
                    {cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-bold uppercase tracking-wide text-gray-500">Search</label>
                <Input data-testid="restock-search" className="h-11 mt-1" value={q}
                  onChange={(e) => setQ(e.target.value)} placeholder="Store name or code" />
              </div>
            </div>
          </Card>

          <Card className="rounded-2xl overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                <AlertTriangle className="h-4 w-4 text-brand-red" />
                <span data-testid="restock-count">{filtered.length}</span> store(s) need re-arrangement
              </div>
            </div>
            {loading ? (
              <div className="p-8 text-center text-gray-500">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="p-8 text-center text-gray-500" data-testid="restock-empty">
                All stores have &ge; {threshold} days of cover. Nothing needs restocking.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-3">Store</th>
                      <th className="text-left px-4 py-3">Zone / City</th>
                      <th className="text-right px-4 py-3">BIB (units)</th>
                      <th className="text-right px-4 py-3">CO2 (units)</th>
                      <th className="text-right px-4 py-3">Weakest</th>
                      <th className="text-right px-4 py-3">Days cover</th>
                      <th className="text-right px-4 py-3">Avg cups/day</th>
                      <th className="text-right px-4 py-3">Band</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered.map(r => (
                      <tr key={r.store_id} data-testid={`restock-row-${r.store_code}`} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-semibold text-gray-900">{r.store_name}</div>
                          <div className="text-xs text-gray-500">{r.store_code}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          <div>{r.zone || "—"}</div>
                          <div className="text-xs text-gray-500">{r.city || ""}</div>
                        </td>
                        <td className="px-4 py-3 text-right font-mono">
                          {Number(r.bib_on_hand_units || 0).toFixed(1)}
                          <div className="text-xs text-gray-500">~{r.bib_glasses_left} glasses</div>
                        </td>
                        <td className="px-4 py-3 text-right font-mono">
                          {Number(r.co2_on_hand_units || 0).toFixed(1)}
                          <div className="text-xs text-gray-500">~{r.co2_glasses_left} glasses</div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Badge className="bg-brand-red/10 text-brand-red border-brand-red/20 gap-1">
                            <Package className="h-3 w-3" /> {r.weakest_item || "—"}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right font-mono font-bold">
                          {r.days_of_cover !== null && r.days_of_cover !== undefined
                            ? `${Number(r.days_of_cover).toFixed(1)}d`
                            : "—"}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-700">{r.avg_daily_sales}</td>
                        <td className="px-4 py-3 text-right">
                          <Badge className={`${bandColor(r.band)} border`}>{r.band.toUpperCase()}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      </main>
    </div>
  );
}
